import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { isDestroyed, isDestroying } from '@ember/destroyable';
import { hash } from '@ember/helper';
import { modifier } from 'ember-modifier';
/**
 * Utility component for helping with scrolling in any direction within
 * any of the 4 directions: up, down, left, right.
 *
 * This can be used to auto-scroll content as new content is inserted into the scrollable area, or possibly to bring focus to something on the page.
 */ export class Scroller extends Component<{
    /**
   * A containing element is required - in this case, a div.
   * It must be scrollable for this component to work, but can be customized.
   *
   * By default, this element will have some styling applied:
   *   overflow: auto;
   *
   * By default, this element will have tabindex="0" to support keyboard usage.
   *
   * The scroll-behavior is "auto", which can be controlled via CSS
   * https://developer.mozilla.org/en-US/docs/Web/CSS/scroll-behavior
   *
   */ Element: HTMLDivElement;
    Blocks: {
        default: [{
                /**
         * Scroll the content to the bottom
         *
         * ```gjs
         * import { Scroller } from 'ember-primitives';
         *
         * <template>
         *   <Scroller as |s|>
         *      ...
         *
         *      {{ (s.scrollToBottom) }}
         *   </Scroller>
         * </template>
         * ```
         */ scrollToBottom: () => void;
                /**
         * Scroll the content to the top
         *
         * ```gjs
         * import { Scroller } from 'ember-primitives';
         *
         * <template>
         *   <Scroller as |s|>
         *      ...
         *
         *      {{ (s.scrollToTop) }}
         *   </Scroller>
         * </template>
         * ```
         */ scrollToTop: () => void;
                /**
         * Scroll the content to the left
         *
         * ```gjs
         * import { Scroller } from 'ember-primitives';
         *
         * <template>
         *   <Scroller as |s|>
         *      ...
         *
         *      {{ (s.scrollToLeft) }}
         *   </Scroller>
         * </template>
         * ```
         */ scrollToLeft: () => void;
                /**
         * Scroll the content to the right
         *
         * ```gjs
         * import { Scroller } from 'ember-primitives';
         *
         * <template>
         *   <Scroller as |s|>
         *      ...
         *
         *      {{ (s.scrollToRight) }}
         *   </Scroller>
         * </template>
         * ```
         */ scrollToRight: () => void;
            }];
    };
}> {
    withinElement: HTMLDivElement;
    ref = modifier((el1: HTMLDivElement)=>{
        this.withinElement = el1;
    });
    #frame: number;
    scrollToBottom = ()=>{
        if (this.#frame) {
            cancelAnimationFrame(this.#frame);
        }
        this.#frame = requestAnimationFrame(()=>{
            if (isDestroyed(this) || isDestroying(this)) return;
            this.withinElement.scrollTo({
                top: this.withinElement.scrollHeight,
                behavior: 'auto'
            });
        });
    };
    scrollToTop = ()=>{
        if (this.#frame) {
            cancelAnimationFrame(this.#frame);
        }
        this.#frame = requestAnimationFrame(()=>{
            if (isDestroyed(this) || isDestroying(this)) return;
            this.withinElement.scrollTo({
                top: 0,
                behavior: 'auto'
            });
        });
    };
    scrollToLeft = ()=>{
        if (this.#frame) {
            cancelAnimationFrame(this.#frame);
        }
        this.#frame = requestAnimationFrame(()=>{
            if (isDestroyed(this) || isDestroying(this)) return;
            this.withinElement.scrollTo({
                left: 0,
                behavior: 'auto'
            });
        });
    };
    scrollToRight = ()=>{
        if (this.#frame) {
            cancelAnimationFrame(this.#frame);
        }
        this.#frame = requestAnimationFrame(()=>{
            if (isDestroyed(this) || isDestroying(this)) return;
            this.withinElement.scrollTo({
                left: this.withinElement.scrollWidth,
                behavior: 'auto'
            });
        });
    };
    static{
        template(`
    <div tabindex="0" ...attributes {{this.ref}}>
      {{yield
        (hash
          scrollToBottom=this.scrollToBottom
          scrollToTop=this.scrollToTop
          scrollToLeft=this.scrollToLeft
          scrollToRight=this.scrollToRight
        )
      }}
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
